import { useEffect, useMemo, useState } from "react";
import { useToast } from "@aidkitorg/component-library";
import { ArrowTrendingUpIcon } from "@heroicons/react/24/outline";
import { Dropdown } from "../Components/Dropdown";
import type { DistroBranch } from "../CollabWrapper";
import { useDialog } from "@aidkitorg/component-library";

export function BranchDropdown(props: {
  branches: DistroBranch[];
  currentBranchId: number | undefined;
  onCreateBranch?: (name: string) => Promise<void>;
  onSwitchBranch: (branchId: number) => void;
  onDeleteBranch?: (branchId: number) => Promise<void>;
  onResetToPublished?: () => Promise<void>;
  disabled?: boolean;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();
  const dialog = useDialog();

  useEffect(() => {
    const currentBranch = props.branches.find(b => b.id === props.currentBranchId);
    const url = new URL(window.location.href);
    if (currentBranch && !currentBranch.is_dev_branch) {
      url.searchParams.set('branch', String(currentBranch.id));
    } else {
      url.searchParams.delete('branch');
    }
    window.history.replaceState({}, '', url.toString());

    return () => {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('branch');
      window.history.replaceState({}, '', cleanUrl.toString());
    };
  }, [props.currentBranchId, props.branches]);

  const currentBranch = props.branches.find(b => b.id === props.currentBranchId);
  const currentBranchLabel = currentBranch?.is_dev_branch ? 'default' : (currentBranch?.name || 'branch');

  const handleCreateBranch = async () => {
    if (!props.onCreateBranch) return;
    const name = await dialog.prompt({
      title: 'New branch',
      message: 'Branch will be created from the published version. Enter a name for the new branch:',
    });
    if (name === null) return; // user cancelled
    if (!name.trim()) {
      toast({ description: 'Branch name cannot be empty', variant: 'error' });
      return;
    }

    const trimmed = name.trim();
    if (['dev', 'default'].includes(trimmed.toLowerCase())) {
      toast({ description: `"${trimmed}" is a reserved branch name`, variant: 'error' });
      return;
    }
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
      toast({ description: 'Branch name can only contain letters, numbers, hyphens, and underscores', variant: 'error' });
      return;
    }

    setIsCreating(true);
    try {
      await props.onCreateBranch(trimmed);
    } catch (e) {
      toast({ description: 'Failed to create branch', variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBranch = async (e: React.MouseEvent, branch: DistroBranch) => {
    e.stopPropagation();
    if (!props.onDeleteBranch) return;
    const typed = await dialog.prompt({
      title: 'Delete branch',
      message: (
        <div className="space-y-2 mt-2">
          <p className="text-red-600 text-sm font-medium">This cannot be undone.</p>
          <p className="text-sm text-gray-700">
            Type <code className="bg-gray-100 px-1 rounded select-all">{branch.name}</code> to confirm:
          </p>
        </div>
      ),
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (typed === null) return;
    if (typed !== branch.name) {
      toast({ description: 'Branch name did not match; deletion cancelled', variant: 'error' });
      return;
    }
    await props.onDeleteBranch(branch.id);
  };

  const sortedBranches = useMemo(() => [...props.branches].sort((a, b) => {
    if (a.is_dev_branch) return -1;
    if (b.is_dev_branch) return 1;
    return 1;
  }), [props.branches]);

  const options = [
    ...sortedBranches.map(branch => ({
      label: (
        <span className="flex items-center gap-2 w-full">
          <span className="flex-1">
            {branch.is_dev_branch ? 'default' : branch.name}
          </span>
          {branch.id === props.currentBranchId && (
            <span className="text-xs text-green-600 font-medium">current</span>
          )}
          {!branch.is_dev_branch && props.onDeleteBranch && (
            <span
              role="button"
              className="text-xs text-red-400 hover:text-red-600 cursor-pointer ml-1"
              onClick={(e) => handleDeleteBranch(e, branch)}
              title="Delete branch">
              x
            </span>
          )}
        </span>
      ),
      callback: () => {
        if (branch.id !== props.currentBranchId) {
          props.onSwitchBranch(branch.id);
        }
      }
    })),
    ...(props.onCreateBranch ? [{
      label: isCreating ? 'Creating...' : '+ New Branch',
      callback: () => handleCreateBranch(),
    }] : []),
    ...(props.onResetToPublished ? [{
      label: `Reset branch "${currentBranchLabel}" to Published` as string,
      callback: async () => {
        const ok = await dialog.confirm({
          title: 'Reset to published',
          message: 'Discard all changes and reset to the published version? This cannot be undone.',
          confirmLabel: 'Reset',
          variant: 'danger',
        });
        if (!ok) return;
        await props.onResetToPublished!();
      },
    }] : []),
  ]

  return (
    <div className={props.disabled ? 'pointer-events-none opacity-50' : ''}>
      <Dropdown
        label={
          <span className="flex items-center gap-1 text-sm">
            <ArrowTrendingUpIcon className="h-4 w-4" />
            {currentBranchLabel}
          </span>
        }
        color="blue"
        colorIntensity={100}
        className="border-0 bg-transparent"
        usePopper
        options={options}
      />
      {dialog.dialogNode}
    </div>
  );
}
